import React, { useCallback, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Divider,
  IconButton,
  Icons,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@ublend-npm/aulaui-core';
import { createStyles } from './ComposePost.styles';
import { getInitials } from '@app/utils/user';
import { useDispatch } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import useEnabledLTIIntegrations from '@app/hooks/useEnabledLTIIntegrations';
import {
  cleanState,
  clearAllSessionFiles,
  isPersistable,
} from '@ublend-npm/aula-editor';
import { resetAllFileScanStatuses } from '@core/actions/data/fileScan';
import uuid from '@core/utils/uuid';
import feedPostTemplates from './feedPostTemplates';
import isEmptyContent from '../utils/isEmptyContent';
import PostMobileWrapper from '../PostMobile/PostMobileWrapper';
import { ANNOUNCEMENTS } from '@core/constants/postFeedType';
import { getCommonRoomPath } from '@app/actions/navigation';
import pushRoute from '@app/utils/routing';
import { DEFAULT_POST_PLACEHOLDER, POST } from '../text';

export type CreatePostAuthor = {
  firstName?: string;
  lastName?: string;
  avatarUrl?: string;
  initials?: string;
};

type ComposePostProps = {
  author?: CreatePostAuthor;
  placeholder?: string;
  isEducator: boolean;
  isSavingPost: boolean;
  createPost: (postData: any) => void;
  spaceId: string;
  editorConfig: any;
  EditorComponent: any;
  feedType: string;
  onBack?: () => void;
};

const ComposePost = ({
  author,
  placeholder = DEFAULT_POST_PLACEHOLDER,
  isEducator,
  isSavingPost,
  createPost,
  spaceId,
  editorConfig,
  EditorComponent,
  feedType,
  onBack,
}: ComposePostProps) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const initials = getInitials({
    firstName: author?.firstName || '',
    lastName: author?.lastName || '',
  });

  const editorRef = useRef(null);
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [uploadInProgress, setUploadInProgress] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [editorState, setEditorState] = useState(cleanState);
  const [correlationId, setCorrelationId] = useState<string>(uuid());
  // Check if the screen is mobile
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const commonRoomPath = getCommonRoomPath(spaceId);

  const resetComponentState = () => {
    setEditorState(cleanState);
    setCorrelationId(uuid());
    clearAllSessionFiles();
    dispatch(resetAllFileScanStatuses());
  };

  const handleCreatePost = async (additionalDataWithSchedulingDate?: any) => {
    try {
      await createPost({
        spaceId,
        content: editorState,
        correlationId,
        feedType,
        ...additionalDataWithSchedulingDate,
      });
      setMessage('A new post has been added');
      resetComponentState();
    } catch (error) {
      setMessage('The post has not been added');
      console.log(error);
    } finally {
      if (isMobile) {
        pushRoute(commonRoomPath);
      }
    }
  };

  const filteredEditorConfig = useEnabledLTIIntegrations(editorConfig);
  filteredEditorConfig.correlationId = correlationId;

  const handleEditorChange = useCallback(
    (state) => {
      setEditorState(state);
    },
    [setEditorState]
  );

  placeholder =
    (feedType === ANNOUNCEMENTS &&
      'Share important information in this space') ||
    DEFAULT_POST_PLACEHOLDER;

  const loading = isSavingPost || isSubmitting;

  const { aulaEditorFtResponsive202512 } = useFlags();
  // Check if the submit button should be disabled. If submit is disabled, show the unsaved changes alert.
  const submitDisabled =
    !isPersistable(editorState, filteredEditorConfig) ||
    loading ||
    isEmptyContent(editorState);

  // Editor component
  const editorComponent = (
    <Box
      sx={{
        ...styles.editorWrapper,
        p: isSavingPost ? '0 0 0 3rem' : '0',
      }}
    >
      <EditorComponent
        key={correlationId}
        type="createpost"
        ref={editorRef}
        config={filteredEditorConfig}
        editorState={editorState}
        placeholder={placeholder}
        onChange={handleEditorChange}
        isSubmitting={loading}
        onFilesStatusChange={({ isUpdating, hasError }) => {
          setUploadInProgress(isUpdating);
          setIsSubmitting(isUpdating && !hasError);
        }}
        readOnly={isSavingPost}
        isAssignmentsEnabled
        templates={feedPostTemplates}
        contentType="post"
        correlationId={correlationId || ''}
        responsiveFlag={aulaEditorFtResponsive202512}
      />
    </Box>
  );

  if (isMobile) {
    return (
      <PostMobileWrapper
        mode="create"
        onBack={onBack}
        onSubmit={() => {
          handleCreatePost();
        }}
        loading={loading || uploadInProgress}
        submitDisabled={submitDisabled}
        showUnsavedChangesAlert={!submitDisabled}
      >
        {editorComponent}
      </PostMobileWrapper>
    );
  }

  return (
    <Box sx={styles.card}>
      <Box sx={styles.toolbar}>
        <Avatar
          altText={author?.firstName + ' ' + author?.lastName}
          fallbackText={initials}
          kind="circular"
          src={author?.avatarUrl}
          sx={styles.avatar}
        />
        <Box sx={styles.editorContainer}>{editorComponent}</Box>
      </Box>
      <Divider />
      <Box sx={styles.body}>
        <Box sx={styles.actions}>
          <Tooltip title="Schedule for later">
            <span>
              <IconButton
                aria-label="Schedule for later"
                disabled
                size="small"
                sx={styles.scheduleButton}
              >
                <Icons.Schedule />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            kind="primary"
            type="button"
            loading={loading || uploadInProgress}
            disabled={
              !isPersistable(editorState, filteredEditorConfig) ||
              loading ||
              isEmptyContent(editorState)
            }
            sx={styles.postButton}
            onClick={handleCreatePost}
          >
            {/* Both label and loader are overlapped when loading is true. to fix it */}
            {loading || uploadInProgress ? '' : POST}
          </Button>
          <Box sx={styles.visuallyHidden} aria-live="polite" role="status">
            {message}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ComposePost;
